import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { DropView } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { PageShell } from '../components/BrandChrome'
import { Alert, Button, Card, Subtitle, Title } from '../components/ui'
import { errorMessage, formatBytes, formatWhen } from '../lib/format'
import { formatPhoneHint } from '../lib/phone'

export function DropDetailPage() {
  const { dropId = '' } = useParams()
  const location = useLocation()
  const { user, loading } = useAuth()
  const [drop, setDrop] = useState<DropView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const stateShare = (location.state as { shareUrl?: string } | null)?.shareUrl
  const shareUrl = `${window.location.origin}/d/${dropId}`

  useEffect(() => {
    if (!user?.mfa_enrolled || !dropId) return
    let cancelled = false
    ;(async () => {
      try {
        const d = await api.getDrop(dropId)
        if (!cancelled) setDrop(d)
      } catch (err) {
        if (!cancelled) setError(errorMessage(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user, dropId])

  async function onRevoke() {
    if (!dropId) return
    if (!window.confirm('Revoke this drop? Recipients will lose access immediately.')) return
    setBusy(true)
    setError(null)
    try {
      await api.me()
      const d = await api.revokeDrop(dropId)
      setDrop(d)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function copyShare() {
    const text = stateShare || shareUrl
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        throw new Error('clipboard unavailable')
      }
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Non-secure contexts and some mobile browsers block clipboard; fall back.
      window.prompt('Copy this share link:', text)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-sm text-slate-500">Loading…</p>
      </PageShell>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  if (!user.mfa_enrolled) return <Navigate to="/mfa/setup" replace />

  return (
    <PageShell homeTo="/drops">
      <div className="mb-4">
        <Title>Drop detail</Title>
        <Subtitle>
          <Link to="/drops" className="underline">
            Back to list
          </Link>
        </Subtitle>
      </div>

      {error ? <Alert className="mb-4">{error}</Alert> : null}
      {!drop ? (
        <p className="text-sm text-slate-500">Loading drop…</p>
      ) : (
        <div className="space-y-4">
          <Card>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-slate-500">Status</dt>
                <dd className="font-medium capitalize">{drop.status}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Recipient</dt>
                <dd>{formatPhoneHint(drop.phone_last4)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">TTL</dt>
                <dd>{drop.ttl_hours} hours</dd>
              </div>
              <div>
                <dt className="text-slate-500">Expires</dt>
                <dd>{formatWhen(drop.expires_at)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Created</dt>
                <dd>{formatWhen(drop.created_at)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Size</dt>
                <dd>
                  {drop.object_count} files · {formatBytes(drop.total_bytes)}
                </dd>
              </div>
            </dl>

            {drop.status === 'active' || drop.status === 'draft' ? (
              <div className="mt-4 space-y-2 rounded-lg bg-slate-50 p-3">
                <div className="text-xs font-medium uppercase text-slate-500">Share link</div>
                <p className="break-all font-mono text-xs">{stateShare || shareUrl}</p>
                <p className="text-xs text-slate-500">
                  Only share this page link. Never treat storage download URLs as the secret.
                </p>
                <Button type="button" variant="secondary" onClick={() => void copyShare()}>
                  {copied ? 'Copied' : 'Copy share link'}
                </Button>
              </div>
            ) : null}

            {drop.status === 'active' ? (
              <div className="mt-4">
                <Button type="button" variant="danger" disabled={busy} onClick={() => void onRevoke()}>
                  {busy ? 'Revoking…' : 'Revoke drop'}
                </Button>
              </div>
            ) : null}
          </Card>

          <Card>
            <h2 className="mb-3 text-lg font-semibold">Files</h2>
            {!drop.objects?.length ? (
              <p className="text-sm text-slate-500">No object metadata on this response.</p>
            ) : (
              <ul className="divide-y text-sm">
                {drop.objects.map((o) => (
                  <li key={o.object_id} className="flex justify-between gap-2 py-2">
                    <span className="break-all font-mono text-xs">{o.relative_path}</span>
                    <span className="shrink-0 text-slate-500">{formatBytes(o.size_bytes)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </PageShell>
  )
}
